import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { aiPodService, type AiPod as AiPodDto } from '../../../services/aiPodService';
import { tenantService } from '../../../services/tenantService';
import { aiPodTenantMapping } from '../../../services/aiPodTenantMapping';
import { toast } from 'react-toastify';
import AiPodModal from './components/AiPodModal';
import AiPodTenantModal from './components/AiPodTenantModal';
import PodSubscribersModal from './components/PodSubscribersModal';
import { Page, PageHeader, Section, Stack } from '../../../components/layout/primitives';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { TextField } from '../../../components/ui/Field';

/** Readable list of what a pod still needs configured (from its readiness). */
function setupMissingLabel(missing: string[]): string {
  return missing
    .map((m) => (m === 'workflow' ? 'workflow' : m === 'chatbot' ? 'chatbot' : 'agent type on every workflow step'))
    .join(' and ');
}

const AiPod: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAiPod, setSelectedAiPod] = useState<AiPodDto | undefined>(undefined);

  const [aiPods, setAiPods] = useState<AiPodDto[]>([]);
  const [loading, setLoading] = useState(false);
  const page = 1;
  const [searchQuery, setSearchQuery] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [tenantModalOpen, setTenantModalOpen] = useState(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedAiPodId, setSelectedAiPodId] = useState<string>('');
  const [subscribersPod, setSubscribersPod] = useState<AiPodDto | null>(null);

  const refreshList = async () => {
    setLoading(true);
    try {
      const data = await aiPodService.listAiPods({
        page,
        search: searchQuery || '',
      });
      setAiPods(data.aiPods || []);
    } catch (e) {
      console.error('Failed to load AI pods:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      refreshList();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery]);

  const loadTenants = async () => {
    try {
      const res: any = await tenantService.list();
      setTenants(res?.tenants || []);
    } catch (err) {
      console.error('Failed to load tenants', err);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const saveTenantData = async (tenantIds: string[], aiPodId: string) => {
    try {
      await aiPodTenantMapping.createTenantAiPodMapping({ tenantIds, aiPodId });
      toast.success('Tenant assignment updated');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update tenant assignment');
    }
  };

  const openCreateModal = () => {
    setSelectedAiPod(undefined);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const openEditModal = (pod: AiPodDto) => {
    setSelectedAiPod(pod);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const askDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await aiPodService.deleteAiPod(deleteId);
      toast.success('Deleted successfully');
      await refreshList();
    } catch (e) {
      console.error('AI pod delete failed:', e);
      toast.error('Failed to delete AI pod');
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <Page contentClassName="pt-0 pb-8">
      <div className="border-b border-white/30 bg-white/80 shadow-sm backdrop-blur-sm">
        <PageHeader
          className="py-6"
          titleClassName="truncate bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-3xl font-bold text-transparent md:text-3xl"
          subtitleClassName="mt-2 text-agentic-text-secondary text-base"
          title="AI Pods"
          subtitle="Bundle agent types into ordered pipelines tenants can purchase as one unit."
          aside={
            <div className="flex w-full max-w-xl flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
              <div className="relative w-full max-w-sm flex-1">
                <TextField tone="default-focus-primary"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search AI pods…"
                  className="w-full rounded-xl border py-2 pl-10 pr-3 focus:ring-2"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
              </div>

              <Button tone="primary-gradient"
                onClick={openCreateModal}
                className="shrink-0 rounded-xl px-4 py-2 transition-all"
              >
                + New AI Pod
              </Button>
            </div>
          }
        />
      </div>

      <Section className="py-8">
        <Stack space="lg">
          {loading ? (
            <div className="py-16 text-center">Loading…</div>
          ) : aiPods.length === 0 ? (
            <div className="py-16 text-center">
              <div className="mb-4 text-6xl">📦</div>
              <h3 className="mb-2 text-xl font-semibold text-agentic-text-primary">No AI pods found</h3>
              <p className="text-agentic-text-secondary">Create an AI pod to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {aiPods.map((pod) => (
                <Card
                  key={pod.id}
                  hover
                  className="rounded-2xl"
                  title={
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 flex-1 truncate">{pod.name || pod.code}</span>
                      <div className="flex items-center gap-2">
                        <Button tone="custom"
                          type="button"
                          onClick={() =>
                            navigate(pod.channel === 'chat' ? `/super-ai-pod/${pod.id}/chat` : `/super-ai-pod/${pod.id}/configure`)
                          }
                          className="rounded-lg border border-agentic-primary-blue/30 px-2 py-1 text-sm text-agentic-primary-blue hover:bg-agentic-primary-blue/5"
                          title={pod.channel === 'chat' ? 'Configure chatbot' : 'Configure workflow'}
                        >
                          Configure
                        </Button>
                        <Button tone="outline"
                          type="button"
                          onClick={() => openEditModal(pod)}
                          className="rounded-lg border px-2 py-1 text-sm"
                          title="Edit"
                        >
                          Edit
                        </Button>
                        <Button tone="danger-soft"
                          type="button"
                          onClick={() => askDelete(pod.id)}
                          className="rounded-lg border px-2 py-1 text-sm"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  }
                  subtitle={`${pod.code} • ${pod.isActive ? 'Active' : 'Inactive'} • ${
                    pod.channel === 'chat' ? 'Chat' : pod.channel === 'both' ? 'Workflow + chat' : 'Workflow'
                  } • ${pod.agentTypes?.length || 0} agent type(s)`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-agentic-primary-blue to-agentic-primary-blue-light text-white">
                      <span className="text-lg">📦</span>
                    </div>
                    <div className="text-sm text-agentic-text-secondary">
                      {pod.shortDescription || pod.description || `Created ${new Date(pod.createdAt || '').toLocaleString()}`}
                    </div>
                  </div>
                  {pod.agentTypes?.length ? (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {[...pod.agentTypes]
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((ref, idx) => (
                          <Badge tone="primary-soft"
                            key={ref.id}
                            className="rounded-full px-2 py-0.5 text-xs"
                          >
                            {idx + 1}. {ref.agentType.name}
                          </Badge>
                        ))}
                    </div>
                  ) : null}
                  {pod.readiness && !pod.readiness.ready && (
                    <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Setup needed: configure the {setupMissingLabel(pod.readiness.missing)} before assigning tenants.
                    </p>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <Button tone="outline"
                      className="shrink-0 rounded-xl border px-4 py-2"
                      onClick={() => setSubscribersPod(pod)}
                    >
                      Subscribers
                    </Button>
                    <Button tone="primary-gradient"
                      className="shrink-0 rounded-xl px-4 py-2 transition-all"
                      onClick={() => {
                        if (!pod.isActive) {
                          toast.warning('AI pod is inactive');
                        } else if (pod.readiness && !pod.readiness.ready) {
                          toast.warning(`Configure the ${setupMissingLabel(pod.readiness.missing)} before assigning tenants`);
                        } else {
                          setSelectedAiPodId(pod.id);
                          setTenantModalOpen(true);
                        }
                      }}
                    >
                      Assign Tenant
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Stack>
      </Section>

      {isModalOpen && (
        <AiPodModal
          isOpen={isModalOpen}
          isEditing={isEditing}
          aiPodDetails={selectedAiPod}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedAiPod(undefined);
          }}
          refreshList={refreshList}
        />
      )}

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteId(null);
        }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Delete this AI pod? Every tenant subscribed to it will be cancelled at the end of their current billing period, after which their workflow and chatbot are paused."
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />

      <PodSubscribersModal pod={subscribersPod} tenants={tenants} onClose={() => setSubscribersPod(null)} />

      <AiPodTenantModal
        isOpen={tenantModalOpen}
        onClose={() => setTenantModalOpen(false)}
        onSave={saveTenantData}
        tenants={tenants}
        aiPodId={selectedAiPodId || ''}
      />
    </Page>
  );
};

export default AiPod;
